import React, {useEffect, useRef, useState} from "react";
import Swal from "sweetalert2";
import {organisationService} from "../../../services/organisation.service";
import {Toast} from "../../../utils/Toast";
import {sanitizeHtml} from "../../../utils/sanitize";
import {IUserField} from "../../../interfaces/user-fields.interface";
import {useTranslation} from "react-i18next";
import {FullPageLoader} from "../../../utils/FullPageLoader";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import {ImportUserFieldModal} from "./ImportUserFields";
import {userFieldService} from "../../../services/user-field.service";
import {IPaginationInfo} from "../../../interfaces/pagination-info.interface";
import moment from "moment";
import {Button, Input, Space, Table} from 'antd';
import type {ColumnType} from 'antd/es/table';
import type {FilterConfirmProps} from 'antd/es/table/interface';
import {SearchOutlined} from '@ant-design/icons';

interface ImportedField {
    csvFileName: string;
    fieldName: string;
    value: string;
    memberUserName: string;
    member?: any;
    organizationField?: any;
    createdAt: string;
    organization: string;
}

export const UserFieldTab: React.FC<any> = (props) => {
    const [userFields, setUserFields] = useState<IUserField[]>([]);
    const [fullLoader, setFullLoader] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedMemberTab, setSelectedMemberTab] = useState<string>('fields');
    const [importedData, setImportedData] = useState<any>();
    const [importedDataPagination, setImportedDataPagination] = useState<IPaginationInfo>()
    const [fileNames, setFileNames] = useState<any[]>([])
    const [selectedFile, setSelectedFile] = useState<string>('Show All');
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [tableFilters, setTableFilters] = useState<Record<string, string>>({});
    const searchInput = useRef<any>(null);
    const {t} = useTranslation('', {keyPrefix: 'OwnOrganisationProfileTabs'});

    useEffect(() => {
        getUserFields()
        getOrganizationCsvFiles()
    }, [])

    const getOrganizationCsvFiles = async () => {
        const res: any = await userFieldService.getOrgProcessedCsvFiles(props.orgId)
        if (res.payload.fileNames.length > 0) {
            const temp = ['Show All', ...res.payload.fileNames];
            setFileNames(temp);
            setSelectedFile((prev) => (prev ? prev : 'Show All')); // keep if already selected, else default
            setFullLoader(false);
        }
    }
    const getImportedFields = async (page: number = 1, accumulatedData: any[] = [], fileName?: string) => {
        const res: any = await userFieldService.getImportedFieldData(props.orgId, page, fileName);
        if (res.statusCode === 201) {
            setFullLoader(false);
            const formatted = res.payload.fieldValues.records.map((item: any) => {
                const meta = item.metaData ? JSON.parse(item.metaData) : {};
                return {
                    key: item._id,
                    value: item.value,
                    csvFileName: item.csvFileName,
                    organization: item.organization,
                    memberUserName: meta.memberUserName,
                    fieldName: meta.fieldName,
                    organizationField: item.organizationField,
                    member: item.member,
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt,
                };
            });
            const updatedData = page == 1 ? [...formatted] : [...accumulatedData, ...formatted];
            setImportedDataPagination(res.payload.fieldValues.paginationInfo);
            setImportedData([...updatedData]);
            if (updatedData.length < res.payload.fieldValues.paginationInfo.totalRecords) {
                await getImportedFields(res.payload.fieldValues.paginationInfo.currentPage + 1, updatedData);
            }
        }
    };
    const getUserFields = async () => {
        setFullLoader(true);
        const res: any = await organisationService.getOrganizationFields(props.orgId);
        if (res.statusCode == 200) {
            setUserFields(res.payload.fields)
        }
        setFullLoader(false);
    }
    const addUserFields = () => {
        setUserFields([...userFields, {_id: '', label: ''}])
    }

    const importNewFields = () => {
        setShowImportModal(true)
    }
    const handleFieldChange = (e: any, index: number) => {
        userFields[index].label = e.target.value
        setUserFields([...userFields])
    }
    const deleteUserField = async (item: any, index: number) => {
        if (item._id) {
            Swal.fire({
                title: t('deleteUserField'),
                text: t('areYouSureDelete'),
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t('accept'),
                cancelButtonText: t('reject'),
            }).then(async (result: any) => {
                if (result) {
                    if (result.isConfirmed) {
                        const res: any = await organisationService.deleteOrganizationUserField(item._id)
                        if (res.statusCode == 200) {
                            userFields.splice(index, 1)
                            setUserFields([...userFields])
                            Toast.fire({
                                icon: "success",
                                title: res.message,
                            });
                        }
                    }
                }
            });
        } else {
            userFields.splice(index, 1)
            setUserFields([...userFields])
        }
    }
    const saveUserField = async (item: any, index: number) => {
        if (item.label.length === 0) {
            Toast.fire({
                icon: "warning",
                title: t('cannotSaveEmpty'),
            });
            return
        }
        if (item._id) {
            const res: any = await organisationService.updateOrganizationFields(item._id, {
                label: item.label.trim()
            })
            if (res.statusCode == 200) {
                Toast.fire({
                    icon: "success",
                    title: res.message,
                });
                userFields[index] = res.payload.field;
                setUserFields([...userFields])
            }
        } else {
            const res: any = await organisationService.createOrganizationFields({
                organization: props.orgId,
                label: item.label.trim()
            })
            if (res.statusCode == 201) {
                Toast.fire({
                    icon: "success",
                    title: res.message,
                });
                userFields[index] = res.payload.field;
                setUserFields([...userFields])
            }
        }
    }
    const handleMemberTabSelection = (e: string) => {
        setSelectedMemberTab(e)
        if (e == 'imported') {
            setFullLoader(true);
            getImportedFields(1)
        } else {
            getUserFields()
        }
    }
    const closeImportModal = (data: boolean) => {
        setShowImportModal(false)
        if (data) {
            getImportedFields();
        }
    }
    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: string
    ) => {
        const value = selectedKeys[0] ?? '';
        // set controlled filter for this column
        setTableFilters(prev => ({...prev, [dataIndex]: value}));
        setSearchText(value);
        setSearchedColumn(dataIndex);
        confirm();
    };
    const handleReset = (
        clearFilters: (() => void) | undefined,
        dataIndex?: string,
    ) => {
        if (clearFilters) clearFilters();
        // Only clear global search if the user is resetting the currently searched column
        if (dataIndex && searchedColumn === dataIndex) {
            setSearchText('');
            setSearchedColumn('');
        }
    };

    const getColumnSearchProps = (
        dataIndex: keyof ImportedField,
        placeholder: string
    ): ColumnType<ImportedField> => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => (
            <div style={{padding: 8}}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${placeholder}`}
                    value={selectedKeys[0]}
                    onChange={e =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex as string)}
                    style={{marginBottom: 8, display: 'block'}}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex as string)}
                        icon={<SearchOutlined/>}
                        size="small"
                        style={{width: 90}}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            // Clear this column's filters
                            handleReset(clearFilters, dataIndex as string);
                            // apply the cleared filter to the table
                            confirm({closeDropdown: false});
                        }}
                        size="small"
                        style={{width: 90}}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}}/>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase())
                : false,
        onFilterDropdownOpenChange: visible => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: text =>
            searchedColumn === dataIndex ? (
                <span
                    // highlight matched text using inline replacement wrapped in a span.
                    // Using dangerouslySetInnerHTML because we replaced matches with HTML.
                    // We ensure value is string and escape is not used — be mindful if cell values can contain HTML.
                    dangerouslySetInnerHTML={{
                        __html: text
                            ? sanitizeHtml(text.toString())
                                .replace(
                                    new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
                                    (match: any) => `<span style="background-color: #ffc069;">${match}</span>`
                                )
                            : ''
                    }}
                />
            ) : (
                text
            ),
    });
    const columns: ColumnType<ImportedField>[] = [
        {
            title: t('filename'),
            dataIndex: 'csvFileName',
            key: 'csvFileName',
            sorter: (a, b) => a.csvFileName.localeCompare(b.csvFileName),
            sortDirections: ['ascend', 'descend'],
            ...getColumnSearchProps('csvFileName', t('filename')),
        },
        {
            title: t('userField'),
            dataIndex: 'fieldName',
            key: 'fieldName',
            sorter: (a, b) => a.fieldName.localeCompare(b.fieldName),
            sortDirections: ['ascend', 'descend'],
            ...getColumnSearchProps('fieldName', t('userField')),
        },
        {
            title: t('value'),
            dataIndex: 'value',
            key: 'value',
            sorter: (a, b) => a.value.localeCompare(b.value),
            sortDirections: ['ascend', 'descend'],
            ...getColumnSearchProps('value', t('value')),
        },
        {
            title: t('username'),
            dataIndex: 'memberUserName',
            key: 'memberUserName',
            sorter: (a, b) => a.memberUserName.localeCompare(b.memberUserName),
            sortDirections: ['ascend', 'descend'],
            ...getColumnSearchProps('memberUserName', t('username')),
        },
        {
            title: t('status'),
            key: 'status',
            render: (_, record) => {
                const statusKey = record.member && record.organizationField ? 'success' : 'failed';
                return t(statusKey); // ✅ shows translated text
            },
            filters: [
                {text: t('success'), value: 'success'},
                {text: t('failed'), value: 'failed'},
            ],
            onFilter: (value, record) => {
                const statusKey = record.member && record.organizationField ? 'success' : 'failed';
                return statusKey === value;
            },
            sorter: (a, b) => {
                const aKey = a.member && a.organizationField ? 'success' : 'failed';
                const bKey = b.member && b.organizationField ? 'success' : 'failed';
                return aKey.localeCompare(bKey);
            },
            sortDirections: ['ascend', 'descend'],
        },
        {
            title: t('timestamp'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt: string) =>
                moment(createdAt).format('MMMM Do YYYY, h:mm:ss a'),
            sorter: (a, b) =>
                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            sortDirections: ['ascend', 'descend'],
        },
    ];

    return <div className="up-themebox">
        {fullLoader && <FullPageLoader/>}
        <div className="up-membersarea">
            <div className="up-adduserfieldbtnholder"
                 style={{float: "none", display: "flex", alignItems: 'center', justifyContent: 'flex-end', gap: 12}}>
                <button className=' up-btn' onClick={importNewFields} style={{marginRight: '8px !important'}}>
                    {t('importCSVFile')}
                </button>
                <button className=' up-btn' onClick={addUserFields}>{t('addUserFields')}</button>
            </div>
            <div className="up-adduserfieldmain">
                <Tabs defaultActiveKey="fields" onSelect={(e: any) => handleMemberTabSelection(e)}>
                    <Tab eventKey="fields" title={t('userFields')}>
                        <div style={{marginTop: 24, marginBottom: 24}}>

                            {userFields.length > 0 && userFields.map((item, index) => (
                                <div className="up-editprofileinputs">
                                    <div className="up-adduserfieldinputholder">
                                        <div className="form-group">
                                            <div className="up-inputwithicon">
                                                <i className="icon-envelop"></i>
                                                <input type="text" onChange={e => handleFieldChange(e, index)}
                                                       value={item.label}/>
                                            </div>
                                        </div>
                                        <ul className="up-saveeditbtns">
                                            <li>
                                                <button onClick={() => saveUserField(item, index)}
                                                        className="up-editbtnpencil" type="button">
                                                    {item._id ? t('update') : t('save')}
                                                </button>
                                            </li>
                                            <li>
                                                <button onClick={() => deleteUserField(item, index)}
                                                        className="up-editbtnpencil" type="button">
                                                    {t('delete')}
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>))
                            }
                            {userFields.length === 0 && <h5>{t('noUserFields')}</h5>}
                        </div>
                    </Tab>
                    <Tab eventKey="imported" title={t('importedFields')}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            {fileNames.length > 0 ? <>
                                <div className="form-group" style={{float: 'right', width: 240, marginTop: 16}}>
                                    <label>{t('selectFile')}</label>
                                    <select
                                        className="form-control"
                                        value={selectedFile}
                                        onChange={(e) => {
                                            const selected = e.target.value;
                                            setSelectedFile(selected);
                                            setFullLoader(true);
                                            if (selected === 'Show All') {
                                                getImportedFields(1);
                                            } else {
                                                getImportedFields(1, [], selected);
                                            }
                                        }}
                                    >
                                        {fileNames.map((e, key) => (
                                            <option key={key + e} value={e}>
                                                {e}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </> : <div></div>}
                            <div></div>
                        </div>
                        {importedData?.length > 0 && (
                            <Table
                                rowKey={(record, index) => record.organization + '-' + index}
                                dataSource={importedData}
                                columns={columns}
                                pagination={false}
                                bordered
                            />
                        )}
                        {importedData?.length == 0 &&
                            <h5>{t('noKeysImportedYet')}</h5>}

                    </Tab>
                </Tabs>
            </div>
            {showImportModal && <ImportUserFieldModal id={props.orgId} closeModal={closeImportModal}/>}
        </div>
    </div>
}